import React from 'react';
import { Flight } from '../../types/Quotation';
import { formatDate, formatTime } from '../../utils/date';

interface FlightCardProps {
  flight: Flight & { day: number };
}

const FlightCard: React.FC<FlightCardProps> = ({ flight }) => {
  const getFlightClassIcon = (flightClass?: string) => {
    if (!flightClass) return '✈️';
    const classLower = flightClass.toLowerCase();
    if (classLower.includes('business')) return '💼';
    if (classLower.includes('first')) return '👑';
    if (classLower.includes('economy')) return '✈️';
    return '✈️';
  };

  const getStopsInfo = (stops: number) => {
    if (stops === 0) return { text: 'Non-stop', color: '#27ae60' };
    if (stops === 1) return { text: '1 stop', color: '#f39c12' };
    return { text: `${stops} stops`, color: '#e74c3c' };
  };

  const stopsInfo = getStopsInfo(flight.stops);

  return (
    <div style={{
      border: '1px solid #e1e5e9',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      gap: '16px'
    }}>
      {/* Airline Logo */}
      <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ position: 'relative', width: '48px', height: '48px', marginBottom: '8px' }}>
          <img
            src={`https://cdn.otaprime.com/airline/${flight.provider}.gif`}
            alt={flight.airline}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).nextElementSibling?.setAttribute('style', 'display: flex');
            }}
          />
          {/* Fallback airline icon */}
          <div style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            border: '1px solid #e9ecef',
            position: 'absolute',
            top: 0,
            left: 0
          }}>
            ✈️
          </div>
        </div>
        <div style={{
          fontSize: '10px',
          color: '#6c757d',
          textAlign: 'center',
          lineHeight: '1.2'
        }}>
          {flight.airline}
        </div>
      </div>

      {/* Flight Details */}
      <div style={{ flex: 1 }}>
        {/* Flight Number and Class */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '16px' }}>{getFlightClassIcon(flight.class)}</span>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
              {flight.provider}-{flight.flightNumber}
            </h4>
            <span style={{
              fontSize: '11px',
              backgroundColor: '#f8f9fa',
              padding: '2px 6px',
              borderRadius: '8px',
              color: '#6c757d'
            }}>
              {flight.class || 'Economy'}
            </span>
          </div>
        </div>

        {/* Route Information */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          {/* From */}
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
              {flight.from}
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>
              {flight.fromAirportName || flight.from}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#34495e', marginTop: '4px' }}>
              {formatTime(flight.departureTime)}
            </div>
          </div>

          {/* Flight Path */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            flex: '0 0 120px',
            padding: '0 16px'
          }}>
            <div style={{
              width: '100%',
              height: '2px',
              backgroundColor: '#bdc3c7',
              position: 'relative',
              marginBottom: '8px'
            }}>
              <div style={{
                position: 'absolute',
                right: '0',
                top: '-4px',
                width: '0',
                height: '0',
                borderLeft: '8px solid #bdc3c7',
                borderTop: '4px solid transparent',
                borderBottom: '4px solid transparent'
              }} />
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#6c757d',
              textAlign: 'center',
              marginBottom: '4px'
            }}>
              {flight.duration}
            </div>
            <div style={{
              fontSize: '10px',
              backgroundColor: stopsInfo.color,
              color: 'white',
              padding: '2px 6px',
              borderRadius: '8px',
              fontWeight: '500'
            }}>
              {stopsInfo.text}
            </div>
          </div>

          {/* To */}
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
              {flight.to}
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>
              {flight.toAirportName || flight.to}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#34495e', marginTop: '4px' }}>
              {formatTime(flight.arrivalTime)}
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '12px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>Date</div>
            <div style={{ fontSize: '12px', fontWeight: '500' }}>
              {formatDate(flight.departureTime)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>Aircraft</div>
            <div style={{ fontSize: '12px', fontWeight: '500' }}>
              {flight.aircraft || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightCard;
